import React from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography } from '@material-ui/core';
import { Button } from 'm2m.components';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import WarningIcon from '@material-ui/icons/Warning';
import { addCpfCnpjMask } from 'utils/mask';
import { formatIntNumber, formatNumber } from 'utils/number';

export type VendaLoteStatus = 'SUCESSO' | 'PARCIAL' | 'FALHA';

export interface VendaLoteResumo {
  totalProcessados: number;
  totalSucesso: number;
  totalFalha: number;
}

export interface VendaLoteItem {
  operacaoId: string | null;
  operacaoCodigo: string;
  codigoIF: string;
  custodianteDestinoCnpj: string;
  investidorDestinoCpfCnpj: string;
  quantidade: number;
  precoUnitario: number;
  liquidacaoDireta: boolean;
  sucesso: boolean;
  mensagemErro?: string | null;
}

export interface VendaLoteResponse {
  status: VendaLoteStatus;
  mensagem: string;
  resumo: VendaLoteResumo;
  itens: VendaLoteItem[];
}

export interface ComprovanteUploadItem {
  operacaoId: string;
  sucesso: boolean;
  comprovante: {
    id: string;
    operacaoId: string;
    fileName: string;
    extension: string;
    size: number;
    creationDate: string;
    url: string;
  } | null;
  erro: {
    httpStatus: number;
    mensagem: string;
  } | null;
}

export interface ComprovanteUploadResponse {
  status: VendaLoteStatus;
  mensagem: string;
  resumo: VendaLoteResumo;
  itens: ComprovanteUploadItem[];
}

export interface VendaFeedbackData {
  venda: VendaLoteResponse;
  comprovantes?: ComprovanteUploadResponse;
}

interface Props {
  open: boolean;
  onClose: () => void;
  data: VendaFeedbackData;
}

const statusConfig: Record<VendaLoteStatus, { icon: React.ReactNode; color: string }> = {
  SUCESSO: {
    icon: <CheckCircleOutlineIcon style={{ fontSize: 40, color: '#4caf50' }} />,
    color: '#4caf50',
  },
  PARCIAL: {
    icon: <WarningIcon style={{ fontSize: 40, color: '#ff9800' }} />,
    color: '#ff9800',
  },
  FALHA: {
    icon: <ErrorOutlineIcon style={{ fontSize: 40, color: '#f44336' }} />,
    color: '#f44336',
  },
};

const tituloByStatus: Record<VendaLoteStatus, string> = {
  SUCESSO: 'Venda em lote processada',
  PARCIAL: 'Venda em lote processada parcialmente',
  FALHA: 'Venda em lote falhou',
};

const agruparItensFalha = (itens: VendaLoteItem[]) => {
  const falhas = itens.filter((i) => !i.sucesso);
  const mapa = new Map<string, VendaLoteItem[]>();
  falhas.forEach((item) => {
    const key = item.codigoIF;
    if (!mapa.has(key)) mapa.set(key, []);
    mapa.get(key)!.push(item);
  });
  return Array.from(mapa.entries()).map(([codigo, destinos]) => ({ codigo, destinos }));
};

interface ComprovanteComDados {
  vendaItem: VendaLoteItem;
  erroMensagem: string;
}

const agruparComprovantesFalha = (data: VendaFeedbackData): Map<string, ComprovanteComDados[]> => {
  const mapa = new Map<string, ComprovanteComDados[]>();
  if (!data.comprovantes) return mapa;

  const comprovantesFalha = data.comprovantes.itens.filter((i) => !i.sucesso);
  comprovantesFalha.forEach((compItem) => {
    const vendaItem = data.venda.itens.find((v) => v.operacaoId === compItem.operacaoId);
    if (!vendaItem) return;

    const key = vendaItem.codigoIF;
    if (!mapa.has(key)) mapa.set(key, []);
    mapa.get(key)!.push({
      vendaItem,
      erroMensagem: compItem.erro?.mensagem ?? 'Erro desconhecido',
    });
  });

  return mapa;
};

const getOverallStatus = (data: VendaFeedbackData): VendaLoteStatus => {
  return data.venda.status;
};

const VendaFeedbackModal: React.FC<Props> = ({ open, onClose, data }) => {
  const overallStatus = getOverallStatus(data);
  const config = statusConfig[overallStatus];
  const ifsComFalha = agruparItensFalha(data.venda.itens);
  const comprovantesFalhaAgrupados = agruparComprovantesFalha(data);
  const temFalhaComprovante = comprovantesFalhaAgrupados.size > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={ifsComFalha.length === 0 && !temFalhaComprovante ? 'sm' : 'md'}
      fullWidth
    >
      <DialogTitle disableTypography>
        <Box display="flex" alignItems="center" style={{ gap: '12px' }}>
          {config.icon}
          <Typography variant="h6" style={{ color: config.color }}>
            {tituloByStatus[overallStatus]}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <Box display="flex" style={{ gap: '24px', marginBottom: '20px' }}>
          {[
            { label: 'Total', value: data.venda.resumo.totalProcessados },
            { label: 'Sucesso', value: data.venda.resumo.totalSucesso, color: '#4caf50' },
            { label: 'Falha', value: data.venda.resumo.totalFalha, color: '#f44336' },
          ].map((card) => (
            <Box
              key={card.label}
              p={1.5}
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                minWidth: '80px',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                {card.label}
              </Typography>
              <Typography variant="h5" style={{ color: card.color }}>
                <strong>{card.value}</strong>
              </Typography>
            </Box>
          ))}
        </Box>

        {ifsComFalha.length > 0 && (
          <>
            <Divider style={{ marginBottom: '16px' }} />
            <Typography variant="subtitle1" style={{ marginBottom: '12px' }}>
              <strong>{'Falhas na venda'}</strong>
            </Typography>
            {ifsComFalha.map((ifErro, ifIndex) => (
              <Box key={ifErro.codigo} mb={ifIndex < ifsComFalha.length - 1 ? 3 : 0}>
                <Box p={1.5} style={{ backgroundColor: '#fafafa', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle1">
                    <strong>{`IF: ${ifErro.codigo}`}</strong>
                  </Typography>
                </Box>

                {ifErro.destinos.map((item, dIndex) => (
                  <Box
                    key={dIndex}
                    mt={1}
                    ml={2}
                    p={1.5}
                    style={{
                      border: '1px solid #ffcdd2',
                      borderRadius: '4px',
                      backgroundColor: '#fff5f5',
                    }}
                  >
                    <Box display="flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          {'Custodiante'}
                        </Typography>
                        <Typography variant="body2">{addCpfCnpjMask(item.custodianteDestinoCnpj)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          {'Investidor'}
                        </Typography>
                        <Typography variant="body2">{addCpfCnpjMask(item.investidorDestinoCpfCnpj)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          {'Quantidade'}
                        </Typography>
                        <Typography variant="body2">{formatIntNumber(item.quantidade)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          {'PU'}
                        </Typography>
                        <Typography variant="body2">{`R$ ${formatNumber(item.precoUnitario)}`}</Typography>
                      </Box>
                    </Box>
                    <Box mt={1}>
                      <Typography variant="body2" color="error">
                        {item.mensagemErro}
                      </Typography>
                    </Box>
                  </Box>
                ))}

                {ifIndex < ifsComFalha.length - 1 && (
                  <Box mt={2}>
                    <Divider />
                  </Box>
                )}
              </Box>
            ))}
          </>
        )}

        {temFalhaComprovante && (
          <>
            <Box mt={3}>
              <Divider style={{ marginBottom: '16px' }} />
            </Box>
            <Typography variant="subtitle1" style={{ marginBottom: '12px' }}>
              <strong>{'Falhas no envio de comprovantes'}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '12px' }}>
              {'A venda foi realizada com sucesso, porém o comprovante não foi enviado para os itens abaixo:'}
            </Typography>
            {Array.from(comprovantesFalhaAgrupados.entries()).map(([codigoIF, itens], groupIndex) => (
              <Box key={codigoIF} mb={groupIndex < comprovantesFalhaAgrupados.size - 1 ? 3 : 0}>
                <Box p={1.5} style={{ backgroundColor: '#fafafa', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle1">
                    <strong>{`IF: ${codigoIF}`}</strong>
                  </Typography>
                </Box>

                {itens.map((item, dIndex) => (
                  <Box
                    key={dIndex}
                    mt={1}
                    ml={2}
                    p={1.5}
                    style={{
                      border: '1px solid #ffe0b2',
                      borderRadius: '4px',
                      backgroundColor: '#fff8e1',
                    }}
                  >
                    <Box display="flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          {'Custodiante'}
                        </Typography>
                        <Typography variant="body2">{addCpfCnpjMask(item.vendaItem.custodianteDestinoCnpj)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          {'Investidor'}
                        </Typography>
                        <Typography variant="body2">
                          {addCpfCnpjMask(item.vendaItem.investidorDestinoCpfCnpj)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          {'Quantidade'}
                        </Typography>
                        <Typography variant="body2">{formatIntNumber(item.vendaItem.quantidade)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          {'PU'}
                        </Typography>
                        <Typography variant="body2">{`R$ ${formatNumber(item.vendaItem.precoUnitario)}`}</Typography>
                      </Box>
                    </Box>
                    <Box mt={1}>
                      <Typography variant="body2" color="error">
                        {item.erroMensagem}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="contained" color="primary" onClick={onClose}>
          {'Fechar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VendaFeedbackModal;
